// ABOUTME: Pinboard sidebar component for pinned insights.
// ABOUTME: Slides in from right with clean card styling, grouped by investigation.

import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Pin, Trash2, Eye, BarChart3, Table2, FileText, LayoutGrid, PieChart, ChevronDown, ChevronRight, MessageSquare, LineChart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { InsightPreviewModal } from './InsightPreviewModal';
import type { PinnedItem, PinnedItemType } from '@/types';

interface PinnedItemCardProps {
  item: PinnedItem;
  onUnpin: (id: string) => void;
  onPreview: () => void;
  isFirstPin?: boolean; // Used for tour targeting
}

// Get the appropriate icon component for each pin type
function getTypeIcon(type: PinnedItemType) {
  switch (type) {
    case 'chart':
      return BarChart3;
    case 'table':
      return Table2;
    case 'summary':
    case 'text':
      return FileText;
    case 'chart-table':
      return LayoutGrid;
    case 'table-summary':
      return Table2; // Table with text badge handled separately
    case 'metric-card':
      return PieChart;
    case 'python-viz':
    case 'python-viz-with-summary':
      return LineChart;
    default:
      return FileText;
  }
}

// Get a color for each type (returns both text and bg classes)
// Uses opacity-based backgrounds that work in both light and dark modes
function getTypeStyles(type: PinnedItemType): { text: string; bg: string; border: string } {
  switch (type) {
    case 'chart':
      return { text: 'text-primary-600 dark:text-primary-400', bg: 'bg-primary-600/10', border: 'border-primary-600/20' };
    case 'table':
      return { text: 'text-success dark:text-green-400', bg: 'bg-success/10', border: 'border-success/20' };
    case 'summary':
    case 'text':
      return { text: 'text-accent-600 dark:text-accent-400', bg: 'bg-accent-600/10', border: 'border-accent-600/20' };
    case 'chart-table':
      return { text: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-600/10', border: 'border-violet-600/20' };
    case 'table-summary':
      return { text: 'text-primary-500 dark:text-primary-400', bg: 'bg-primary-600/10', border: 'border-primary-600/20' };
    case 'metric-card':
      return { text: 'text-accent-500 dark:text-accent-400', bg: 'bg-accent-600/10', border: 'border-accent-600/20' };
    case 'python-viz':
    case 'python-viz-with-summary':
      return { text: 'text-primary-600 dark:text-primary-400', bg: 'bg-primary-600/10', border: 'border-primary-600/20' };
    default:
      return { text: 'text-surface-500', bg: 'bg-surface-500/10', border: 'border-surface-500/20' };
  }
}

// Get human-readable type label
function getTypeLabel(type: PinnedItemType): string {
  switch (type) {
    case 'chart':
      return 'Chart';
    case 'table':
      return 'Table';
    case 'summary':
      return 'Summary';
    case 'text':
      return 'Text';
    case 'chart-table':
      return 'Chart + Table';
    case 'table-summary':
      return 'Table + Summary';
    case 'metric-card':
      return 'Metric';
    case 'python-viz':
      return 'Visualization';
    case 'python-viz-with-summary':
      return 'Viz + Summary';
    default:
      return 'Insight';
  }
}

function PinnedItemCard({ item, onUnpin, onPreview, isFirstPin }: PinnedItemCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const Icon = getTypeIcon(item.type);
  const styles = getTypeStyles(item.type);
  const typeLabel = getTypeLabel(item.type);

  // Check if overview is long enough to need expansion
  const hasLongOverview = item.insightOverview && item.insightOverview.length > 100;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="group relative rounded-sm bg-surface-0 border border-surface-200 hover:bg-surface-50 hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden"
      data-tour={isFirstPin ? 'pinboard-item' : undefined}
    >
      {/* Type badge header */}
      <div className={`px-3 py-1.5 ${styles.bg} border-b ${styles.border} flex items-center justify-between`}>
        <div className="flex items-center gap-1.5">
          <Icon className={`w-3.5 h-3.5 ${styles.text}`} />
          <span className={`text-xs font-medium ${styles.text}`}>{typeLabel}</span>
        </div>
        <span className="text-[10px] text-surface-400">
          {new Date(item.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </span>
      </div>

      {/* Main content */}
      <div className="p-3">
        <h4 className="text-sm font-medium text-surface-800 line-clamp-2 leading-snug">
          {item.title}
        </h4>

        {/* Insight overview with expand/collapse */}
        {item.insightOverview && (
          <div className="mt-2">
            <p className={`text-xs text-surface-500 leading-relaxed ${!isExpanded ? 'line-clamp-2' : ''}`}>
              {item.insightOverview}
            </p>
            {hasLongOverview && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="mt-1 flex items-center gap-0.5 text-[11px] text-surface-400 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                <span>{isExpanded ? 'Show less' : 'Show more'}</span>
              </button>
            )}
          </div>
        )}

      </div>

      {/* Action buttons footer */}
      <div className="px-3 py-2 border-t border-surface-100 flex items-center justify-between bg-surface-50">
        <button
          onClick={onPreview}
          className="flex items-center gap-1.5 px-2 py-1 rounded-sm text-xs text-surface-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
          aria-label="Preview insight"
        >
          <Eye className="w-3.5 h-3.5" />
          <span>Preview</span>
        </button>
        <button
          onClick={() => onUnpin(item.id)}
          className="p-1.5 rounded-sm text-surface-400 hover:text-error hover:bg-error-light transition-colors cursor-pointer"
          aria-label="Unpin"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </motion.div>
  );
}

// Investigation group with collapsible pins
interface InvestigationGroupProps {
  investigationId: string;
  investigationTitle: string;
  pins: PinnedItem[];
  onUnpin: (id: string) => void;
  onPreview: (item: PinnedItem) => void;
  defaultExpanded?: boolean;
  isFirstGroup?: boolean; // Used for tour targeting - first pin in first group gets data-tour attribute
}

function InvestigationGroup({
  investigationTitle,
  pins,
  onUnpin,
  onPreview,
  defaultExpanded = true,
  isFirstGroup = false,
}: InvestigationGroupProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="space-y-1">
      {/* Investigation header - collapsible, positioned at left edge as parent */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 py-1.5 rounded-sm hover:bg-surface-100 transition-colors group -ml-1 pl-1 cursor-pointer"
      >
        <div className="flex items-center justify-center w-5 h-5">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-surface-400 group-hover:text-surface-600 transition-colors" />
          ) : (
            <ChevronRight className="w-4 h-4 text-surface-400 group-hover:text-surface-600 transition-colors" />
          )}
        </div>
        <MessageSquare className="w-4 h-4 text-surface-400" />
        <span className="flex-1 text-left text-xs font-medium text-surface-600 truncate group-hover:text-surface-800 transition-colors">
          {investigationTitle}
        </span>
        <span className="text-[10px] text-surface-400 bg-surface-100 px-1.5 py-0.5 rounded-full mr-1">
          {pins.length}
        </span>
      </button>

      {/* Pins in this investigation - indented to show hierarchy */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="space-y-2 ml-3 pl-3 border-l border-surface-200">
              <AnimatePresence initial={false} mode="popLayout">
                {pins.map((item, index) => (
                  <PinnedItemCard
                    key={item.id}
                    item={item}
                    onUnpin={onUnpin}
                    onPreview={() => onPreview(item)}
                    isFirstPin={isFirstGroup && index === 0}
                  />
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Pinboard() {
  const { pinnedItems, unpinItem, isPinboardOpen, closePinboard } = useApp();
  const [previewItem, setPreviewItem] = useState<PinnedItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const navigate = useNavigate();

  // Group pins by investigation
  const groupedPins = useMemo(() => {
    const groups: Record<string, { title: string; pins: PinnedItem[] }> = {};

    for (const pin of pinnedItems) {
      const invId = pin.sourceInvestigationId || 'unknown';
      const invTitle = pin.sourceInvestigationTitle || 'Unknown Investigation';

      if (!groups[invId]) {
        groups[invId] = { title: invTitle, pins: [] };
      }
      groups[invId].pins.push(pin);
    }

    // Convert to array and sort by most recent pin in each group
    return Object.entries(groups)
      .map(([id, data]) => ({
        investigationId: id,
        investigationTitle: data.title,
        pins: data.pins.sort((a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        ),
      }))
      .sort((a, b) => {
        // Sort groups by most recent pin timestamp
        const aLatest = Math.max(...a.pins.map(p => new Date(p.timestamp).getTime()));
        const bLatest = Math.max(...b.pins.map(p => new Date(p.timestamp).getTime()));
        return bLatest - aLatest;
      });
  }, [pinnedItems]);

  const handlePreview = (item: PinnedItem) => {
    setPreviewItem(item);
  };

  const handleClosePreview = () => {
    setPreviewItem(null);
  };

  const handleUnpinClick = (id: string, title: string) => {
    setDeleteTarget({ id, title });
  };

  const handleUnpinConfirm = () => {
    if (deleteTarget) {
      unpinItem(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  return (
    <>
      <AnimatePresence mode="wait">
        {isPinboardOpen && (
          <motion.aside
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
            className="fixed right-0 top-0 h-full w-80 bg-surface-0 border-l border-surface-200 z-20 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-surface-200">
              <div className="flex items-center gap-2">
                <Pin className="w-4 h-4 text-primary-500" />
                <h2 className="font-semibold text-surface-800">Pinboard</h2>
                <span className="text-xs text-surface-400 bg-surface-100 px-2 py-0.5 rounded-full">
                  {pinnedItems.length}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={closePinboard}
                aria-label="Close pinboard"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Content */}
            <motion.div layoutScroll className="flex-1 overflow-y-auto p-4">
              {pinnedItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center px-4">
                  <div className="w-16 h-16 rounded-sm bg-surface-100 flex items-center justify-center mb-4">
                    <Pin className="w-8 h-8 text-surface-400" />
                  </div>
                  <h3 className="text-sm font-medium text-surface-600 mb-1">
                    No pinned items yet
                  </h3>
                  <p className="text-xs text-surface-400">
                    Pin insights from your investigations to save them here for quick access.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {groupedPins.map((group, index) => (
                    <InvestigationGroup
                      key={group.investigationId}
                      investigationId={group.investigationId}
                      investigationTitle={group.investigationTitle}
                      pins={group.pins}
                      onUnpin={(id) => {
                        const pin = pinnedItems.find(p => p.id === id);
                        handleUnpinClick(id, pin?.title || 'this pin');
                      }}
                      onPreview={handlePreview}
                      defaultExpanded={index === 0} // First group expanded by default
                      isFirstGroup={index === 0} // First group gets tour targeting
                    />
                  ))}
                </div>
              )}
            </motion.div>

            {/* Footer */}
            {pinnedItems.length > 0 && (
              <div className="p-4 border-t border-surface-200">
                <Button
                  variant="secondary"
                  size="sm"
                  className="w-full"
                  onClick={() => navigate('/reports/new')}
                >
                  Create Report from Pins
                </Button>
              </div>
            )}
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Insight Preview Modal */}
      <InsightPreviewModal
        item={previewItem}
        onClose={handleClosePreview}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleUnpinConfirm}
        title="Unpin Insight"
        message={`Are you sure you want to unpin "${deleteTarget?.title}"? You can always pin it again from the investigation.`}
        confirmLabel="Unpin"
      />
    </>
  );
}
